import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import { Badge } from "../ui/badge";
import { Link } from "@tanstack/react-router";
import React, { useEffect, useState } from "react";
import { decode } from "html-entities";
import placeholderImage from "../../assets/articles/grafika-3.png";
import placeholderImageAuthor from "../../assets/logo-ltca-mini.png";
import {
	type FavoriteArticles,
	getFavoriteArticles,
	type ArticleData,
	getAddArticleToFavorites,
} from "@/api/api";
import { LoaderSpinner } from "../ui/LoaderSpinner";
import { HeartDarkIcon } from "../icons/heartFull";
import { HeartIcon } from "../icons/heart";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { getArticleCategoryBadgeStyle } from "@/utils/colorsArticle";
import { FEATURE_FLAG_COVER_CARD_NEW_VISUAL } from "@/pages/Home";

interface ArticleCardV1Props {
	article: ArticleData;
}

const getFavoriteStatus = (
	slug: string | undefined,
	favoriteArticles: FavoriteArticles | null | undefined,
	isAuthenticated: boolean
): "DISABLED" | "IS_FAVORITE" | "NOT_FAVORITE" => {
	if (!isAuthenticated) {
		return "DISABLED";
	}
	if (slug === undefined) {
		return "DISABLED";
	}
	if (favoriteArticles === undefined || favoriteArticles === null) {
		return "DISABLED";
	}
	if (favoriteArticles.favorites.some((article) => article.slug === slug)) {
		return "IS_FAVORITE";
	}
	return "NOT_FAVORITE";
};

const ArticleCardList: React.FC<ArticleCardV1Props> = React.forwardRef(
	(props, ref) => {
		const {
			title,
			date,
			ID,
			author,
			author_photo,
			kategorie,
			excerpt,
			image,
			tagi,
			slug,
		} = props.article;

		const { isAuthenticated } = useAuth();

		const {
			isFetching: isFeatchingFavoriteArticles,
			data: favoriteArticles,
			refetch: refetchFavoriteArticles,
		} = useQuery({
			queryKey: ["favorite-articles"],
			queryFn: () => getFavoriteArticles(),
			staleTime: 1000 * 60 * 5,
			enabled: !!isAuthenticated,
		});

		const {
			isFetching: isFetchingAddToFavorites,
			refetch: refetchAddToFavorites,
		} = useQuery({
			queryKey: ["add_product_to_favorites", slug],
			queryFn: () => getAddArticleToFavorites(slug),
			enabled: false,
		});

		const [loading, setLoading] = useState(false);
		const [favoriteStatus, setFavoriteStatus] = useState<
			"DISABLED" | "IS_FAVORITE" | "NOT_FAVORITE"
		>(getFavoriteStatus(slug, favoriteArticles, isAuthenticated));

		useEffect(() => {
			if (!isFetchingAddToFavorites && !isFeatchingFavoriteArticles) {
				setLoading(false);
			}
		}, [isFetchingAddToFavorites, isFeatchingFavoriteArticles]);

		useEffect(() => {
			setFavoriteStatus(
				getFavoriteStatus(slug, favoriteArticles, isAuthenticated)
			);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [favoriteArticles]);

		const [imageUrl, setImageUrl] = useState(image ?? placeholderImage);

		const shortDescriptionDecoded = decode(excerpt);

		const handleAddToFavorites = async (
			event: React.MouseEvent<HTMLButtonElement>
		): Promise<void> => {
			event.stopPropagation();
			event.preventDefault();
			setLoading(true);
			await refetchAddToFavorites();
			await refetchFavoriteArticles();
		};

		return (
			<div
				ref={ref}
				className="flex flex-col bg-transparent border-b border-dropdownBorder mb-4"
			>
				<div className="relative">
					<div className="absolute z-10 p-3 flex gap-2">
						{kategorie &&
							Array.isArray(kategorie) &&
							kategorie.map((cat) => (
								<>
									{FEATURE_FLAG_COVER_CARD_NEW_VISUAL ? (
										<Badge
											key={`${cat.name}${ID}`}
											className="text-xs font-medium"
											style={getArticleCategoryBadgeStyle(cat.name)}
										>
											{cat.name}
										</Badge>
									) : (
										<Badge
											key={`${cat.name}${ID}`}
											className="text-xs font-medium"
											variant="highlight"
										>
											{cat.name}
										</Badge>
									)}
								</>
							))}
					</div>
					<div className="absolute z-10 right-10 p-3 flex gap-2">
						{tagi &&
							Array.isArray(tagi) &&
							tagi.map((tag) => (
								<Badge
									key={`${tag.name}${ID}`}
									className="text-xs"
									variant="articleTag"
								>
									{tag.name.toUpperCase()}
								</Badge>
							))}
					</div>
					{favoriteStatus !== "DISABLED" && (
						<div className="absolute z-10 right-0 top-0">
							{loading ? (
								<LoaderSpinner className="p-3" size={6} />
							) : (
								<button
									className="p-3"
									onClick={(event) => handleAddToFavorites(event)}
								>
									{favoriteStatus === "IS_FAVORITE" ? (
										<HeartDarkIcon color="white" />
									) : (
										<HeartIcon color="white" />
									)}
								</button>
							)}
						</div>
					)}
					<LazyLoadImage
						alt="title"
						className="rounded-xl h-[126px] xs:h-auto"
						effect="blur"
						src={imageUrl}
						onError={() => {
							setImageUrl(placeholderImage);
						}}
					/>
				</div>
				<div className="pb-3 pt-4 flex flex-col flex-grow">
					<h4 className="font-bold text-lg mb-2">{title}</h4>
					<p className="text-fontseconadry line-clamp-3 text-left mb-4 pr-1">
						{shortDescriptionDecoded}
					</p>
					<div className="flex justify-between gap-1">
						<div className="flex text-sm">
							<img
								alt="author"
								className="my-auto h-10 w-10 max-h-10 max-w-10 rounded-full"
								src={author_photo ?? placeholderImageAuthor}
							/>
							<div className="ml-3 my-auto">
								<div>{author}</div>
								<div className="text-fontseconadry">
									{date}
									{/* {format(articleDate * 1000, "dd.MM.yyyy")} */}
								</div>
							</div>
						</div>
						<Link
							className="my-auto py-2 px-3 bg-white border-2 border-dropdownBorder rounded-xl text-sm font-medium min-w-[101px]"
							to={`/articles/${ID ?? props.article.pivot.post_id}`}
						>
							Czytaj dalej
						</Link>
					</div>
				</div>
			</div>
		);
	}
);

export default ArticleCardList;
